// src/components/admin/pages/TopPicksPool.jsx
import { useState, useEffect } from 'react';
import { Plus, Filter, RefreshCw, Search, Edit2, Eye, AlertTriangle } from 'lucide-react';
import { topPicksPoolApi } from '../../../services/adminApi';
import TopPicksRecordModal from '../TopPicksRecordModal';

const TopPicksPool = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    primary_genre: '',
    status: '',
    search: '',
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Load records on component mount and when filters change
  useEffect(() => {
    loadRecords();
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadRecords = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await topPicksPoolApi.getTopPicksPool(filters);
      setRecords(result.records || []);
    } catch (err) {
      console.error('Failed to load Top Picks Pool records:', err);
      setError(err.message || 'Failed to load records');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
  };

  const handleRefreshTopPicks = async () => {
    if (!confirm('Refresh Top Picks? This clears cached recommendation lists so they regenerate on next access.')) {
      return;
    }
    setRefreshing(true);
    try {
      await topPicksPoolApi.refreshTopPicks();
      await loadRecords();
    } catch (err) {
      console.error('Failed to refresh Top Picks:', err);
      setError(`Failed to refresh Top Picks: ${err.message}`);
    } finally {
      setRefreshing(false);
    }
  };

  const handleMakePassive = async (record) => {
    if (!confirm(`Make "${record.title}" passive? This will remove it from active Top Picks generation.`)) {
      return;
    }

    try {
      await topPicksPoolApi.makeRecordPassive(record.id);
      await loadRecords(); // Reload to show updated status
    } catch (err) {
      console.error('Failed to make record passive:', err);
      setError(`Failed to make record passive: ${err.message}`);
    }
  };

  const getStatusBadge = (status) => {
    const baseClasses = 'px-2 py-1 text-xs rounded-full';
    if (status === 'active') {
      return `${baseClasses} bg-green-100 text-green-800`;
    } else if (status === 'passive') {
      return `${baseClasses} bg-gray-100 text-gray-800`;
    }
    return `${baseClasses} bg-yellow-100 text-yellow-800`;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-text-primary mb-2">Top Picks Pool</h1>
            <p className="text-text-secondary">Manage the pool of books for Top Picks recommendations</p>
          </div>
        </div>
        <div className="bg-bg-surface rounded-lg border border-gray-200 p-12 text-center">
          <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text-secondary">Loading Top Picks Pool...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-text-primary mb-2">Top Picks Pool</h1>
            <p className="text-text-secondary">Manage the pool of books for Top Picks recommendations</p>
          </div>
        </div>
        <div className="bg-bg-surface rounded-lg border border-red-200 p-6">
          <div className="flex items-center space-x-3 text-red-600 mb-4">
            <AlertTriangle className="w-5 h-5" />
            <h3 className="font-medium">Error Loading Records</h3>
          </div>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={loadRecords}
            className="btn-secondary"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary mb-2">Top Picks Pool</h1>
          <p className="text-text-secondary">Manage the pool of books for Top Picks recommendations</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Add Book</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-bg-surface rounded-lg border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search books..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300"
            />
          </div>

          {/* Primary Genre */}
          <select
            value={filters.primary_genre}
            onChange={(e) => handleFilterChange('primary_genre', e.target.value)}
            className="border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300"
          >
            <option value="">All Genres</option>
            <option value="picture_books">Picture Books</option>
            <option value="adventure">Adventure</option>
            <option value="fantasy">Fantasy</option>
            <option value="friendship">Friendship</option>
            <option value="educational">Educational</option>
          </select>

          {/* Status */}
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="passive">Passive</option>
          </select>

          {/* Refresh Button */}
          <button
            onClick={handleRefreshTopPicks}
            disabled={refreshing}
            className="flex items-center justify-center space-x-2 px-3 py-2 text-primary-600 hover:bg-primary-50 rounded text-sm border border-primary-200 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>{refreshing ? 'Refreshing...' : 'Refresh Top Picks'}</span>
          </button>
        </div>
      </div>

      {/* Records Table */}
      <div className="bg-bg-surface rounded-lg border border-gray-200 overflow-hidden">
        {records.length === 0 ? (
          <div className="p-12 text-center text-text-secondary">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Filter className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-text-primary mb-2">No Records Found</h3>
            <p className="mb-4">
              {Object.values(filters).some(f => f)
                ? 'No records match your current filters.'
                : 'No books have been added to the Top Picks Pool yet.'}
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary"
            >
              Add First Book
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Book</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Genre</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rank</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {records.map((record) => (
                  <tr key={record.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        {record.cover_url ? (
                          <img
                            src={record.cover_url}
                            alt={record.title}
                            className="w-12 h-16 object-cover rounded"
                          />
                        ) : (
                          <div className="w-12 h-16 bg-gray-200 rounded flex items-center justify-center">
                            <span className="text-gray-400 text-xs">No Image</span>
                          </div>
                        )}
                        <div>
                          <div className="font-medium text-text-primary">{record.title}</div>
                          <div className="text-sm text-text-secondary">{record.author}</div>
                          {record.note && (
                            <div className="text-xs text-text-secondary mt-1 italic">&ldquo;{record.note}&rdquo;</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-text-primary">
                      <div className="font-medium">{record.primary_genre.replace('_', ' ')}</div>
                      {record.secondary_genres && record.secondary_genres.length > 0 && (
                        <div className="text-xs text-text-secondary mt-1 flex flex-wrap gap-1">
                          {record.secondary_genres.map(genre => (
                            <span key={genre} className="bg-gray-100 px-2 py-0.5 rounded text-xs">
                              {genre.replace('_', ' ')}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-text-primary font-medium">#{record.rank}</td>
                    <td className="px-6 py-4">
                      <span className={getStatusBadge(record.status)}>
                        {record.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setEditingRecord(record)}
                          className="p-1 text-text-secondary hover:text-primary-600 hover:bg-primary-50 rounded"
                          title="Edit record"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        {record.status === 'active' && (
                          <button
                            onClick={() => handleMakePassive(record)}
                            className="p-1 text-text-secondary hover:text-yellow-600 hover:bg-yellow-50 rounded"
                            title="Make passive"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <TopPicksRecordModal
        isOpen={showCreateModal || !!editingRecord}
        onClose={() => {
          setShowCreateModal(false);
          setEditingRecord(null);
        }}
        record={editingRecord}
        onSuccess={loadRecords}
      />

    </div>
  );
};

export default TopPicksPool;
